/**
 * Code colouring for a documentation body (pass 400.82: shared by the public
 * docs script and the PDF export, which used to style `.hljs-*` spans that were
 * never produced because nothing on its side ever ran highlight.js).
 */
import hljs from 'highlight.js/lib/core'
import php from 'highlight.js/lib/languages/php'
import javascript from 'highlight.js/lib/languages/javascript'
import css from 'highlight.js/lib/languages/css'
import bash from 'highlight.js/lib/languages/bash'
import json from 'highlight.js/lib/languages/json'
import xml from 'highlight.js/lib/languages/xml'

hljs.registerLanguage('php', php)
hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('css', css)
hljs.registerLanguage('bash', bash)
hljs.registerLanguage('json', json)
hljs.registerLanguage('html', xml)

/** Colour one `<pre><code>` and tint the lines its `data-highlight` calls out. */
export function highlightCode(codeEl) {
    hljs.highlightElement(codeEl)
    highlightLines(codeEl)
}

/**
 * Tint the lines a code block calls out (`<pre data-highlight="3-5,9">`).
 *
 * The tint has to be per LINE, and highlight.js hands back one flat blob of markup whose
 * spans freely cross newlines (a block comment is a single span spanning five lines). So
 * the markup is split on the newline and every tag still open at the break is closed and
 * re-opened on the next line - anything less produces a document with unbalanced spans,
 * which the browser silently "repairs" by re-nesting the rest of the block inside it.
 *
 * Runs AFTER hljs, on the highlighted output, so the token colours survive.
 */
function highlightLines(codeEl) {
    const pre = codeEl.closest('pre')
    const spec = pre?.getAttribute('data-highlight')
    if (!spec) return

    const wanted = parseLineSpec(spec)
    if (!wanted.size) return

    const lines = splitHighlightedLines(codeEl.innerHTML)

    codeEl.innerHTML = lines
        .map((line, i) => {
            const active = wanted.has(i + 1) ? ' is-highlighted' : ''

            // A non-breaking-space keeps an empty highlighted line from collapsing to
            // zero height, which would put a gap in the middle of the tinted band.
            return `<span class="doc-code-line${active}">${line || '&nbsp;'}</span>`
        })
        // JOINED WITH NOTHING, NOT WITH A NEWLINE. `.doc-code-line` is a BLOCK, so
        // every line already ends where the next one begins; the separator was a
        // literal newline landing between two block boxes inside `white-space:
        // pre`, which the text engine renders as an empty line of its own. The
        // result was a highlighted block set at double leading while an unmarked
        // one beside it - same page, same rules, no `data-highlight` and so no
        // spans - sat at single. Only visible once a page carried both.
        .join('')
}

/** "3-5,9" into the set {3,4,5,9}. */
function parseLineSpec(spec) {
    const lines = new Set()

    spec.split(',').forEach(part => {
        const [from, to] = part.trim().split('-').map(n => parseInt(n, 10))
        if (!Number.isFinite(from)) return

        const last = Number.isFinite(to) ? to : from
        for (let n = from; n <= last && n - from < 500; n++) lines.add(n)
    })

    return lines
}

/** Highlighted markup into one balanced HTML string per source line. */
function splitHighlightedLines(html) {
    const tokens = html.match(/<\/?span[^>]*>|\n|[^<\n]+|</g) || []
    const open = []
    const lines = []
    let current = ''

    tokens.forEach(token => {
        if (token === '\n') {
            // Close what is open, break the line, re-open it on the next one.
            current += open.map(() => '</span>').join('')
            lines.push(current)
            current = open.join('')

            return
        }

        if (token.startsWith('</span')) {
            open.pop()
        } else if (token.startsWith('<span')) {
            open.push(token)
        }

        current += token
    })

    lines.push(current)

    // hljs keeps the trailing newline that lived inside <pre>; it is not a line.
    if (lines.length > 1 && lines[lines.length - 1].trim() === '') lines.pop()

    return lines
}
